import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  TablePagination,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AdminPanelSettings as RoleIcon,
  Search as SearchIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Security as SecurityIcon,
  Visibility as ViewIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { hasWritePermission } from '../../utils/menuFilter';
const RoleMaster = () => {
  const canWriteRole = hasWritePermission('user-management.create-roles');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
  const [editRoleDialogOpen, setEditRoleDialogOpen] = useState(false);
  const [viewRoleDialogOpen, setViewRoleDialogOpen] = useState(false);
  const [deleteConfirmDialogOpen, setDeleteConfirmDialogOpen] = useState(false);
  const [statusConfirmDialogOpen, setStatusConfirmDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleName, setRoleName] = useState('');
  const [tabValue, setTabValue] = useState(0);

  // Sample role data
  const [roleData, setRoleData] = useState([
    {
      id: 1,
      roleName: 'Administrator',
      createdDate: '2024-01-15',
      status: 'Active',
      userCount: 5,
    },
    {
      id: 2,
      roleName: 'Manager',
      createdDate: '2024-02-20',
      status: 'Active',
      userCount: 12,
    },
    {
      id: 3,
      roleName: 'Sales Executive',
      createdDate: '2024-03-10',
      status: 'Active',
      userCount: 25,
    },
    {
      id: 4,
      roleName: 'HR Executive',
      createdDate: '2024-04-05',
      status: 'Active',
      userCount: 8,
    },
    {
      id: 5,
      roleName: 'Finance Executive',
      createdDate: '2024-05-12',
      status: 'Active',
      userCount: 10,
    },
    {
      id: 6,
      roleName: 'Customer Support',
      createdDate: '2024-06-18',
      status: 'Active',
      userCount: 18,
    },
    {
      id: 7,
      roleName: 'Data Analyst',
      createdDate: '2024-07-22',
      status: 'Inactive',
      userCount: 3,
    },
    {
      id: 8,
      roleName: 'Operations Executive',
      createdDate: '2024-08-14',
      status: 'Active',
      userCount: 15,
    },
  ]);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  const handleAddRole = () => {
    setRoleName('');
    setAddRoleDialogOpen(true);
  };

  const handleViewRole = (role) => {
    setSelectedRole(role);
    setViewRoleDialogOpen(true);
  };

  const handleEditRole = (role) => {
    setSelectedRole(role);
    setRoleName(role.roleName);
    setEditRoleDialogOpen(true);
  };

  const handleDeleteRole = (role) => {
    setSelectedRole(role);
    setDeleteConfirmDialogOpen(true);
  };

  const confirmDeleteRole = () => {
    setRoleData(prev => prev.filter(item => item.id !== selectedRole.id));
    setDeleteConfirmDialogOpen(false);
    setSelectedRole(null);
  };

  const handleStatusToggle = (role) => {
    setSelectedRole(role);
    setStatusConfirmDialogOpen(true);
  };

  const confirmStatusToggle = () => {
    setRoleData(prev =>
      prev.map(item =>
        item.id === selectedRole.id
          ? { ...item, status: selectedRole.status === 'Active' ? 'Inactive' : 'Active' }
          : item
      )
    );
    setStatusConfirmDialogOpen(false);
    setSelectedRole(null);
  };

  const handleSaveRole = () => {
    if (!roleName.trim()) {
      alert('Please enter a role name');
      return;
    }

    const newRole = {
      id: Math.max(...roleData.map(item => item.id)) + 1,
      roleName: roleName,
      createdDate: new Date().toISOString().split('T')[0],
      status: 'Active',
      userCount: 0,
    };

    setRoleData(prev => [...prev, newRole]);
    setAddRoleDialogOpen(false);
    setRoleName('');
  };

  const handleUpdateRole = () => {
    if (!roleName.trim()) {
      alert('Please enter a role name');
      return;
    }

    setRoleData(prev =>
      prev.map(item =>
        item.id === selectedRole.id
          ? { ...item, roleName: roleName }
          : item
      )
    );

    setEditRoleDialogOpen(false);
    setRoleName('');
    setSelectedRole(null);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPage(0);
  };

  const filteredData = roleData.filter(
    (row) =>
      row.roleName.toLowerCase().includes(search.toLowerCase()) ||
      row.status.toLowerCase().includes(search.toLowerCase())
  );

  const getFilteredByStatus = () => {
    if (tabValue === 0) return filteredData; // All
    if (tabValue === 1) return filteredData.filter(row => row.status === "Active");
    if (tabValue === 2) return filteredData.filter(row => row.status === "Inactive");
    return filteredData;
  };

  const displayData = getFilteredByStatus();
  const activeCount = roleData.filter(row => row.status === "Active").length;
  const inactiveCount = roleData.filter(row => row.status === "Inactive").length;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#536DFE', mb: 0.5 }}>
              Role Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage user roles and permissions
            </Typography>
          </Box>
          {canWriteRole && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={handleAddRole}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1.2,
                textTransform: 'none',
                fontSize: '15px',
                boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
                borderRadius: 2
              }}
            >
              Add Role
            </Button>
          )}
        </Box>
      </Box>

      {/* Search and Tabs */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search roles..."
                value={search}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          {/* Tabs */}
          <Box sx={{ mt: 2 }}>
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => {
                setTabValue(newValue);
                setPage(0);
              }}
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 'auto',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  minHeight: 48,
                  px: 3
                }
              }}
            >
              <Tab label={`All Roles (${roleData.length})`} />
              <Tab label={`Active (${activeCount})`} />
              <Tab label={`Inactive (${inactiveCount})`} />
            </Tabs>
          </Box>
        </CardContent>
      </Card>

      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  backgroundColor: '#536DFE',
                  '& .MuiTableCell-root': {
                    border: 'none'
                  }
                }}>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '16px',
                    py: 3,
                    px: 3
                  }}>
                    #
                  </TableCell>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '16px',
                    py: 3,
                    px: 3
                  }}>
                    Role Name
                  </TableCell>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '16px',
                    py: 3,
                    px: 3
                  }}>
                    Created Date
                  </TableCell>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '16px',
                    py: 3,
                    px: 3
                  }}>
                    Status
                  </TableCell>
                  <TableCell sx={{
                    color: '#fff',
                    fontWeight: 800,
                    fontSize: '16px',
                    py: 3,
                    px: 3
                  }} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayData.length > 0 ? (
                  displayData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((role, index) => (
                      <TableRow
                        key={role.id}
                        hover
                        sx={{
                          '&:nth-of-type(odd)': {
                            backgroundColor: '#f9f9f9'
                          },
                          '&:hover': {
                            backgroundColor: '#f0f7ff !important'
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 700, fontSize: '14px', py: 3, px: 3 }}>
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell sx={{ py: 3, px: 3 }}>
                          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: '14px' }}>
                            {role.roleName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3, px: 3 }}>
                          <Typography variant="body2">
                            {role.createdDate}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 3, px: 3 }}>
                          {canWriteRole ? (
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={role.status === 'Active'}
                                  onChange={() => handleStatusToggle(role)}
                                  color="success"
                                  size="medium"
                                />
                              }
                              label={
                                <Chip
                                  label={role.status}
                                  size="small"
                                  color={role.status === 'Active' ? 'success' : 'default'}
                                  sx={{ fontWeight: 600 }}
                                />
                              }
                              sx={{ m: 0 }}
                            />
                          ) : (
                            <Chip
                              label={role.status}
                              size="small"
                              color={role.status === 'Active' ? 'success' : 'default'}
                              sx={{ fontWeight: 600 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ py: 3, px: 3 }}>
                          <Box display="flex" gap={0.5} justifyContent="center">
                            <Tooltip title="View Role Details">
                              <IconButton
                                onClick={() => handleViewRole(role)}
                                size="small"
                                sx={{ border: '1px solid #e0e0e0' }}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canWriteRole && (
                              <Tooltip title="Edit Role">
                                <IconButton
                                  onClick={() => handleEditRole(role)}
                                  color="primary"
                                  size="small"
                                  sx={{ border: '1px solid #e0e0e0' }}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {canWriteRole && (
                              <Tooltip title="Delete Role">
                                <IconButton
                                  onClick={() => handleDeleteRole(role)}
                                  color="error"
                                  size="small"
                                  sx={{ border: '1px solid #e0e0e0' }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No roles found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {displayData.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 15, 20]}
              component="div"
              count={displayData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* Add Role Dialog */}
      <Dialog
        open={addRoleDialogOpen}
        onClose={() => setAddRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 3
        }}>
          <RoleIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Add New Role
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <TextField
            fullWidth
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="Enter role name"
            required
            autoFocus
            size="medium"
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={() => setAddRoleDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRole}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
            }}
          >
            Add Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog
        open={editRoleDialogOpen}
        onClose={() => setEditRoleDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 3
        }}>
          <EditIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Edit Role
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <TextField
            fullWidth
            label="Role Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="Enter role name"
            required
            autoFocus
            size="medium"
            sx={{
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
              }
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={() => setEditRoleDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpdateRole}
            variant="contained"
            startIcon={<EditIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
            }}
          >
            Update Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Role Dialog */}
      <Dialog
        open={viewRoleDialogOpen}
        onClose={() => setViewRoleDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 3
        }}>
          <ViewIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Role Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          {selectedRole && (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#536DFE', mb: 1 }}>
                  {selectedRole.roleName}
                </Typography>
                <Chip
                  icon={selectedRole.status === 'Active' ? <ActiveIcon sx={{ fontSize: 16 }} /> : <InactiveIcon sx={{ fontSize: 16 }} />}
                  label={selectedRole.status}
                  size="medium"
                  color={selectedRole.status === 'Active' ? 'success' : 'error'}
                  sx={{ fontWeight: 600, fontSize: '14px' }}
                />
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    Created Date
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedRole.createdDate}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, mb: 1 }}>
                    Users Assigned
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {selectedRole.userCount} users
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={() => setViewRoleDialogOpen(false)}
            variant="contained"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(83, 109, 254, 0.3)',
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialogOpen}
        onClose={() => setDeleteConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#f44336',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 3
        }}>
          <WarningIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete the role <strong>"{selectedRole?.roleName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            This action cannot be undone. All users assigned to this role will lose their role permissions.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={() => setDeleteConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDeleteRole}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
            }}
          >
            Delete Role
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Toggle Confirmation Dialog */}
      <Dialog
        open={statusConfirmDialogOpen}
        onClose={() => setStatusConfirmDialogOpen(false)}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#536DFE',
          color: 'white',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          py: 3,
          px: 3
        }}>
          <WarningIcon sx={{ fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.1rem' }}>
            {selectedRole?.status === 'Active' ? 'Deactivate Role' : 'Activate Role'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: 3, pt: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to {selectedRole?.status === 'Active' ? 'deactivate' : 'activate'} the role <strong>"{selectedRole?.roleName}"</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedRole?.status === 'Active'
              ? 'Deactivating this role will prevent users from accessing role-specific features.'
              : 'Activating this role will restore access to role-specific features for assigned users.'
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2, borderTop: '1px solid #e0e0e0', bgcolor: '#f8f9fa' }}>
          <Button
            onClick={() => setStatusConfirmDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmStatusToggle}
            variant="contained"
            color={selectedRole?.status === 'Active' ? 'warning' : 'success'}
            startIcon={selectedRole?.status === 'Active' ? <InactiveIcon /> : <ActiveIcon />}
            sx={{
              textTransform: 'none',
              px: 3,
              py: 1,
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          >
            {selectedRole?.status === 'Active' ? 'Deactivate' : 'Activate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleMaster;
